/**
 * Matrice de permissions par rôle (CTO-FRONT-002 niveau d'accès).
 *
 * Synthèse :
 * - admin_holding : super-admin, accès cross-filiale TOTAL
 * - auditeur      : lecture seule cross-filiale (aucun create/edit/delete)
 * - dg_filiale    : DG sa filiale (tout sauf Holding consolidé)
 * - daf           : Finance + RH + Reporting + créer user (vision salaires)
 * - manager       : Opérations + CRM (pas Finance, pas RH salaires)
 * - chef_equipe   : Opérations équipe uniquement (Royal LT)
 *
 * Helper `can(role, action)` retourne true/false pour cacher/afficher
 * un bouton, une nav item, une section.
 */
export type Role = 'admin_holding' | 'auditeur' | 'dg_filiale' | 'daf' | 'manager' | 'rh' | 'acheteur' | 'commercial' | 'chef_equipe';

export type Action =
  // Holding (consolidation cross-filiale)
  | 'holding.view'
  // Finance
  | 'finance.kpis.view'
  | 'finance.journal.view'   | 'finance.journal.create' | 'finance.journal.validate'
  | 'finance.invoices.view'  | 'finance.invoices.create' | 'finance.invoices.validate' | 'finance.invoices.pay'
  | 'finance.tarifs.manage'
  // CRM générique (compat ; signifie "voit au moins un type")
  | 'crm.view'               | 'crm.manage'
  // CRM granulaire par type — acheteur voit suppliers, commercial voit clients+prospects
  | 'crm.suppliers.view'     | 'crm.suppliers.manage'
  | 'crm.clients.view'       | 'crm.clients.manage'
  | 'crm.prospects.view'     | 'crm.prospects.manage'
  | 'crm.pipeline.view'      | 'crm.pipeline.manage'
  // RH
  | 'rh.employees.view'      | 'rh.employees.manage'   | 'rh.salary.view'
  // Users (comptes connexion)
  | 'users.view'             | 'users.create'
  // Opérations
  | 'operations.view'        | 'operations.manage'     | 'operations.delete'
  // Annuler un bon de livraison (07/2026) : la direction seulement
  | 'operations.beton.annuler_bl'
  // Tasks
  | 'tasks.assign'
  // Reporting
  | 'reporting.export';

/**
 * Matrice rôle × action. Chaque case = true si autorisé.
 * Source de vérité côté frontend (le backend a ses propres @Roles + RLS pour
 * la sécurité — cette matrice sert juste à l'UX : cacher les boutons qui
 * échoueraient avec un 403 de toute façon).
 */
const MATRIX: Record<Role, Set<Action>> = {
  admin_holding: new Set<Action>([
    'holding.view',
    'finance.kpis.view', 'finance.journal.view', 'finance.journal.create', 'finance.journal.validate',
    'finance.invoices.view', 'finance.invoices.create', 'finance.invoices.validate', 'finance.invoices.pay',
    'finance.tarifs.manage',
    'crm.view', 'crm.manage',
    'crm.suppliers.view', 'crm.suppliers.manage',
    'crm.clients.view', 'crm.clients.manage',
    'crm.prospects.view', 'crm.prospects.manage',
    'crm.pipeline.view', 'crm.pipeline.manage',
    'rh.employees.view', 'rh.employees.manage', 'rh.salary.view',
    'users.view', 'users.create',
    'operations.view', 'operations.manage', 'operations.delete', 'operations.beton.annuler_bl',
    'tasks.assign',
    'reporting.export',
  ]),
  auditeur: new Set<Action>([
    'holding.view',
    'finance.kpis.view', 'finance.journal.view', 'finance.invoices.view',
    'crm.view',
    'crm.suppliers.view', 'crm.clients.view', 'crm.prospects.view', 'crm.pipeline.view',
    'rh.employees.view',
    'users.view',
    'operations.view',
    'reporting.export',
  ]),
  dg_filiale: new Set<Action>([
    'finance.kpis.view', 'finance.journal.view', 'finance.journal.create',
    'finance.invoices.view', 'finance.invoices.create', 'finance.invoices.validate',
    'finance.tarifs.manage',
    'crm.view', 'crm.manage',
    'crm.suppliers.view', 'crm.suppliers.manage',
    'crm.clients.view', 'crm.clients.manage',
    'crm.prospects.view', 'crm.prospects.manage',
    'crm.pipeline.view', 'crm.pipeline.manage',
    'rh.employees.view', 'rh.employees.manage', 'rh.salary.view',
    'users.view', 'users.create',
    'operations.view', 'operations.manage', 'operations.delete', 'operations.beton.annuler_bl',
    'tasks.assign',
    'reporting.export',
  ]),
  daf: new Set<Action>([
    'finance.kpis.view', 'finance.journal.view', 'finance.journal.create', 'finance.journal.validate',
    'finance.invoices.view', 'finance.invoices.create', 'finance.invoices.validate', 'finance.invoices.pay',
    'finance.tarifs.manage',
    'crm.view', 'crm.manage',
    'crm.suppliers.view', 'crm.suppliers.manage',
    'crm.clients.view', 'crm.clients.manage',
    'crm.prospects.view',
    'crm.pipeline.view',
    'rh.employees.view', 'rh.employees.manage', 'rh.salary.view',
    'users.view', 'users.create',
    'operations.view', 'operations.manage', 'operations.beton.annuler_bl',
    'tasks.assign',
    'reporting.export',
  ]),
  manager: new Set<Action>([
    'crm.view', 'crm.manage',
    'crm.clients.view', 'crm.clients.manage',
    'crm.prospects.view', 'crm.prospects.manage',
    'crm.pipeline.view', 'crm.pipeline.manage',
    'rh.employees.view',
    'operations.view', 'operations.manage',
    'tasks.assign',
  ]),
  rh: new Set<Action>([
    'rh.employees.view', 'rh.employees.manage', 'rh.salary.view',
    'users.view', 'users.create',
    'reporting.export',
    'tasks.assign',
  ]),
  acheteur: new Set<Action>([
    // Achat : fournisseurs CRM + factures fournisseurs + stocks matières (lecture)
    'crm.view', 'crm.suppliers.view', 'crm.suppliers.manage',
    'finance.invoices.view', 'finance.invoices.create',  // factures fournisseurs uniquement (filtré côté backend)
    'operations.view',                                    // voir stocks matières premières
    'tasks.assign',
    'reporting.export',
  ]),
  commercial: new Set<Action>([
    // Commercial / Marketing : clients + prospects + pipeline + devis/proformas
    'crm.view',
    'crm.clients.view', 'crm.clients.manage',
    'crm.prospects.view', 'crm.prospects.manage',
    'crm.pipeline.view', 'crm.pipeline.manage',
    'finance.invoices.view', 'finance.invoices.create',  // proformas clients (filtré côté backend)
    'finance.tarifs.manage',                              // tarifs négociés clients
    'tasks.assign',
    'reporting.export',
  ]),
  chef_equipe: new Set<Action>([
    'operations.view', 'operations.manage', // limité à son équipe via RLS backend
  ]),
};

/** Rôles connus du tableau (lu par le contrôle des droits écran ↔ serveur, TICKET-2026-004). */
export const ROLES_ECRAN = Object.keys(MATRIX) as Role[];

/** Vérifie si un rôle a le droit d'effectuer une action. */
export function can(role: string | undefined, action: Action): boolean {
  if (!role) return false;
  return MATRIX[role as Role]?.has(action) ?? false;
}

/** Vérifie si un rôle a AU MOINS UNE des actions données. */
export function canAny(role: string | undefined, actions: Action[]): boolean {
  return actions.some((a) => can(role, a));
}

/** Vérifie si un rôle a TOUTES les actions données. */
export function canAll(role: string | undefined, actions: Action[]): boolean {
  return actions.every((a) => can(role, a));
}
